import { useCallback, useEffect, useState } from "react";
import type { OrderStatus, OrderSummary } from "../types/catalog";

type UseOrdersOptions = {
  currentAdminEmail: string | null;
  isAdmin: boolean;
};

export function useOrders({ currentAdminEmail, isAdmin }: UseOrdersOptions) {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [orderListStatus, setOrderListStatus] = useState("");

  const refreshOrders = useCallback(async () => {
    setOrderListStatus(isAdmin ? "Loading orders..." : "Loading your orders...");

    const response = await fetch("/api/orders");
    if (!response.ok) {
      setOrderListStatus("Orders could not be loaded.");
      return;
    }

    const body = await response.json<{ orders: OrderSummary[] }>();

    setOrders(body.orders);
    setOrderListStatus("");
  }, [isAdmin]);

  useEffect(() => {
    void refreshOrders();
  }, [refreshOrders]);

  function prependOrder(order: OrderSummary) {
    setOrders((currentOrders) => [order, ...currentOrders]);
  }

  async function handleOrderStatusChange(orderId: string, status: OrderStatus) {
    setOrderListStatus("Updating order...");

    const response = await fetch(`/api/orders/${orderId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const body = await response.json<{ error?: string }>().catch(() => null);
      const message = body?.error ?? "Order status was not updated.";
      setOrderListStatus(message);
      return { message, ok: false };
    }

    setOrders((currentOrders) =>
      currentOrders.map((order) => (order.id === orderId ? { ...order, status } : order))
    );
    setOrderListStatus("Order updated.");
    return { message: "Order updated.", ok: true };
  }

  async function handleOrderClaim(
    orderId: string
  ): Promise<{ message: string; ok: boolean; orderId: string | null }> {
    setOrderListStatus("Claiming order...");

    const response = await fetch(`/api/orders/${orderId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ claim: true }),
    });

    if (!response.ok) {
      const body = await response.json<{ error?: string }>().catch(() => null);
      const message =
        body?.error ??
        (response.status === 409 ? "Order is already claimed." : "Order could not be claimed.");
      setOrderListStatus(message);
      return { message, ok: false, orderId: null };
    }

    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              claimedBy: currentAdminEmail ?? "admin",
              status:
                order.status === "pending_payment" || order.status === "paid"
                  ? "packing"
                  : order.status,
            }
          : order
      )
    );
    setOrderListStatus("Order claimed.");
    return { message: "Order claimed.", ok: true, orderId };
  }

  async function handleOrderRelease(orderId: string) {
    setOrderListStatus("Releasing order...");

    const response = await fetch(`/api/orders/${orderId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ releaseClaim: true }),
    });

    if (!response.ok) {
      const body = await response.json<{ error?: string }>().catch(() => null);
      const message = body?.error ?? "Order claim could not be released.";
      setOrderListStatus(message);
      return { message, ok: false };
    }

    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              claimedAt: "",
              claimedBy: "",
              status:
                order.status === "packing"
                  ? order.paymentStatus === "paid"
                    ? "paid"
                    : "pending_payment"
                  : order.status,
            }
          : order
      )
    );
    setOrderListStatus("Order released.");
    return { message: "Order released.", ok: true };
  }

  return {
    handleOrderClaim,
    handleOrderRelease,
    handleOrderStatusChange,
    orderListStatus,
    orders,
    prependOrder,
    refreshOrders,
  };
}
